<template>
  <dn-collection
    v-if="shouldRender"
    :cid="placeholderId"
    :suppress-emit="true"
    :suppress-skip-ids="true"
    :class="['dn-segment-collection', backgroundColor?.toLowerCase()]"
    provider="segment"
    :options="segmentOptions"
    :data-streamId="dataStreamId"
    :fnc="onSegmentItemReceived"
  >
    <template #default="{ collection }">
      <dn-life-span-teaser
        v-if="collection && collection.length > 0"
        :cid="placeholderId"
        :items="collection"
        :remove-used="removeUsed"
        :rowlayout="rowlayout"
        :repeat="repeat"
        :image-hide="imageHide"
      >
        <template #default="{ displayItems, layoutPatterns, getCommonCardProps }">
          <dn-set
            v-for="(group, groupIndex) in buildSetGroups(displayItems, layoutPatterns, getCommonCardProps, slots)"
            :key="groupIndex"
            :layout="group.layout"
            :items="group.items"
          />
        </template>
      </dn-life-span-teaser>
    </template>
  </dn-collection>
</template>

<script setup lang="ts">
import type { SegmentCollectionItem } from '~/types/article';
import type { CardProps, GetCommonCardProps, SetGroup, Slot } from '~/types/collection';
import { useProfileLoginStore } from '~/store/profileLogin';

/*
 * Slice the flat displayItems into one group per layout pattern so each pattern
 * renders its own <dn-set>. The global item index is preserved when building
 * card props so DnLifeSpanTeaser's image-hide logic keeps working. The `slots`
 * prop is built per row (via calculateSetRows in slotInjectionProcessor), not
 * per group, so each group claims its own run of slots through a cursor: on
 * mobile a multi-row layout gets one ad slot per row, on desktop calculateSetRows
 * is 1 so each group gets a single slot. DnSet renders the slots via <dn-component>.
 */
const buildSetGroups = (
  displayItems: SegmentCollectionItem[],
  layoutPatterns: string[],
  getCommonCardProps: GetCommonCardProps,
  slots: Slot[] = []
) => {
  const groups: SetGroup[] = [];
  let index = 0;
  let slotCursor = 0;

  for (const layout of layoutPatterns) {
    const slice = displayItems.slice(index, index + layoutItemCount(layout));
    if (slice.length === 0) { break; }

    const items: CardProps[] = slice.map((item, i) => ({
      ...getCommonCardProps(item, index + i),
      teaser_type: computedTeaserType.value,
      ...(item.type === 'video' ? { duration: item.duration } : {})
    }));

    const rowCount = calculateSetRows({ layout, items: [] });
    items.push(...slots.slice(slotCursor, slotCursor + rowCount));
    slotCursor += rowCount;

    groups.push({ layout, items });
    index += slice.length;
  }

  return groups;
};

const props = defineProps({
  placeholder: {
    type: String,
    required: true
  },
  rowlayout: {
    type: String,
    required: false,
    default: 'BBB'
  },
  repeat: {
    type: Number,
    required: false,
    default: 1
  },
  id: {
    type: String,
    required: true
  },
  backgroundColor: {
    type: String,
    required: false,
    default: null
  },
  imageHide: {
    type: Boolean,
    required: false,
    default: false
  },
  removeUsed: {
    type: Boolean,
    required: false,
    default: true
  },
  defaultTopList: {
    type: String,
    required: false,
    default: null
  },
  defaultTopListFallback: {
    type: String,
    required: false,
    default: null
  },
  oneIdFallback: {
    type: String,
    required: false,
    default: null
  },
  testSegments: {
    type: Boolean,
    required: false,
    default: false
  },
  topListControlGroup: {
    type: String,
    required: false,
    default: null
  },
  topListTestGroup: {
    type: String,
    required: false,
    default: null
  },
  type: {
    type: String,
    required: false,
    default: null
  },
  testInternal: {
    type: Boolean,
    required: false,
    default: true
  },
  slots: {
    type: Array as PropType<Slot[]>,
    required: false,
    default: () => []
  }
});

provide('teaser_provider', 'articleTeaser');
provide('prefetch', false);
const profileLoginStore = useProfileLoginStore();

const abTestCookie = useCookie('DNMG_AB_CPP');

const testActive = props.testInternal && props.testSegments;
const { userSegments, isLoading } = testActive
  ? useSegmentReader()
  : { userSegments: ref<string[]>([]), isLoading: ref(false) };

const segmentTestGroup = computed(() => {
  if (!testActive || isLoading.value) { return null; }

  const segments = userSegments.value || [];
  if (segments.includes('editorial_cpp__dn__abtest_0123__test')) { return 'test'; }
  if (segments.includes('editorial_cpp__dn__abtest_0123__control')) { return 'control'; }
  return null;
});

const testGroupLabel = computed(() => (testActive && segmentTestGroup.value) || 'not active');

const computedTeaserType = computed(() => {
  const group = testGroupLabel.value;
  return props.type ? `${props.type} | testActive: ${group}` : `testActive: ${group}`;
});

const shouldRender = computed(() => {
  if (!props.testInternal) { return true; }
  if (props.testSegments) {
    if (isLoading.value) { return false; }
    if (!profileLoginStore.loginStatus.isLoggedIn) { return false; }
    return segmentTestGroup.value !== null;
  }
  return abTestCookie.value === 'B';
});

const activeTopList = computed(() => {
  if (testActive && segmentTestGroup.value) {
    return segmentTestGroup.value === 'test' ? props.topListTestGroup : props.topListControlGroup;
  }
  return props.defaultTopList;
});

const dataStreamId = computed(() => `${activeTopList.value} | ${testGroupLabel.value}`);

const placeholderId = `${props.placeholder}__${props.id}`;
const emitEmptyCollection = () => useBusEmitter(`DnCollection__collection__${placeholderId}`, { items: [] });

const renderResolved = computed(() => !props.testSegments || !isLoading.value);

watch([renderResolved, shouldRender], () => {
  if (renderResolved.value && !shouldRender.value) {
    emitEmptyCollection();
  }
}, { immediate: true });

const onSegmentItemReceived = (items: string[]) => {
  if (items.length === 0) {
    emitEmptyCollection();
  }
};

const toplistLimit = useRuntimeConfig().public.toplistLimit ?? 50;

const segmentOptions = computed(() => {
  const loggedIn = profileLoginStore.loginStatus.isLoggedIn;
  const oneIdFromStore = profileLoginStore.loginStatus.oneId;
  const isControlGroup = testActive && segmentTestGroup.value === 'control';
  const effectiveOneId = (loggedIn && !isControlGroup) ? oneIdFromStore || props.oneIdFallback : props.oneIdFallback;
  return {
    oneId: effectiveOneId,
    toplist: loggedIn ? activeTopList.value : props.defaultTopListFallback,
    removeUsed: props.removeUsed,
    size: null,
    ...(toplistLimit ? { limit: String(toplistLimit) } : {})
  };
});

</script>

<style lang="scss">
.dn-segment-collection {
  --dn-collection_grid-template: var(--dn-segment-collection_grid-template);
  --dn-collection_gap: var(--dn-segment-collection_gap);
  --dn-collection_fill: var(--dn-segment-collection_fill);

  .dn-teaser-card {
    --dn-teaser-card--link-text-decoration: underline;
    --teaser-card--card_border-radius: 4px;
    --teaser-card--card_row-gap: 8px;
  }
}
</style>
